'use client';
import { useState, useRef, useEffect } from 'react';
import { FiUser, FiLogOut, FiUsers, FiCreditCard, FiStar, FiMapPin, FiZap, FiRepeat, FiEye } from 'react-icons/fi';
import Image from 'next/image';
import Link from 'next/link';

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  provider?: string;
  is_verified?: boolean;
}

interface DashboardSidebarProps {
  user: User;
  onLogout: () => void;
  selectedMenu: string;
  onMenuSelect: (menuKey: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
  /** Logged-out preview: hide the Logout button + desktop collapse toggle. */
  preview?: boolean;
}

interface MenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  children?: { key: string; label: string; icon: React.ReactNode }[];
}

const MENU: MenuItem[] = [
  { key: 'profile', label: 'Profile', icon: <FiUser size={22} /> },
  {
    key: 'matches',
    label: 'My Matches',
    icon: <FiUsers size={22} />,
    children: [
      { key: 'matches:direct', label: 'Direct', icon: <FiStar size={18} /> },
      { key: 'matches:district', label: 'District', icon: <FiMapPin size={18} /> },
      { key: 'matches:chain', label: '3-Way', icon: <FiRepeat size={18} /> },
      { key: 'matches:magic', label: 'Magic', icon: <FiZap size={18} /> },
      { key: 'matches:seen', label: 'My Seen Contacts', icon: <FiEye size={18} /> },
    ],
  },
  { key: 'payments', label: 'Payments', icon: <FiCreditCard size={22} /> },
];

export default function DashboardSidebar({ 
  user, 
  onLogout, 
  selectedMenu, 
  onMenuSelect,
  isOpen,
  onClose,
  preview = false,
}: DashboardSidebarProps) {
  const [internalSidebarOpen, setInternalSidebarOpen] = useState(false); // mobile overlay
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false); // desktop collapse
  const [isDesktop, setIsDesktop] = useState(false); // Add state for desktop detection
  
  // Use external state if provided, otherwise use internal state
  const sidebarOpen = isOpen !== undefined ? isOpen : internalSidebarOpen;
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  // Determine desktop state after component mounts to prevent hydration mismatch
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-mount gate; reads window after hydration, effect also subscribes to resize
    setIsDesktop(window.innerWidth >= 1024);

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swipe gesture handlers for mobile
  useEffect(() => {
    function handleTouchStart(e: TouchEvent) {
      if (window.innerWidth >= 1024) return;
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    }
    function handleTouchMove(e: TouchEvent) {
      if (window.innerWidth >= 1024) return;
      if (touchStartX.current === null) return;
      const dx = e.touches[0].clientX - touchStartX.current;
      const dy = Math.abs(e.touches[0].clientY - (touchStartY.current ?? 0));
      // Swipe from left edge to open
      if (!sidebarOpen && touchStartX.current < 40 && dx > 40 && dy < 30) {
        if (onClose) {
          onClose(); // This will be called to open the sidebar
        } else {
          setInternalSidebarOpen(true);
        }
        touchStartX.current = null;
        touchStartY.current = null;
      }
      // Swipe left to close
      if (sidebarOpen && dx < -40 && dy < 30) {
        if (onClose) {
          onClose(); // This will be called to close the sidebar
        } else {
          setInternalSidebarOpen(false);
        }
        touchStartX.current = null;
        touchStartY.current = null;
      }
    }
    function handleTouchEnd() {
      touchStartX.current = null;
      touchStartY.current = null;
    }
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [sidebarOpen, onClose]);

  // Determine sidebar state for desktop
  const sidebarWidth = isDesktop
    ? desktopSidebarCollapsed ? 'w-16' : 'w-64'
    : 'w-64';
  const sidebarTranslate = isDesktop
    ? 'translate-x-0'
    : sidebarOpen ? 'translate-x-0' : '-translate-x-full';

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-zinc-900/20 backdrop-blur-[0.9px] z-40 lg:hidden"
          onClick={() => {
            if (onClose) {
              onClose();
            } else {
              setInternalSidebarOpen(false);
            }
          }}
          aria-label="Close sidebar overlay"
        />
      )}
      
      <aside
        ref={sidebarRef}
        className={`
          ${sidebarWidth} ${sidebarTranslate}
          ${isDesktop ? 'static h-screen z-10' : 'fixed top-0 left-0 h-full z-50'}
          bg-white border-r border-slate-200 shadow-lg flex flex-col transition-all duration-300
        `}
      >
        {/* Collapse/Expand Button - right edge, only on desktop */}
        {!preview && (
          <button
            className="absolute right-[-18px] top-2 z-10 p-2 rounded-full bg-white text-slate-700 hover:bg-slate-50 border border-slate-300 shadow-lg hidden lg:block"
            onClick={() => setDesktopSidebarCollapsed((collapsed) => !collapsed)}
            aria-label={desktopSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {desktopSidebarCollapsed ? <span className="text-lg">&#x25B6;</span> : <span className="text-lg">&#x25C0;</span>}
          </button>
        )}
        
        {/* User Section */}
        <div className={`flex items-center gap-3 mt-8 mb-8 px-2 ${((isDesktop && desktopSidebarCollapsed) || (!isDesktop && !sidebarOpen)) ? 'justify-center' : ''}`}>
          <div className="bg-slate-200 rounded-full w-12 h-12 flex items-center justify-center text-slate-600 text-2xl">
            {user.avatar_url ? (
              <Image
                src={user.avatar_url} 
                alt={user.name}
                width={48}
                height={48}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <FiUser />
            )}
          </div>
          {((isDesktop && !desktopSidebarCollapsed) || (!isDesktop && sidebarOpen)) && (
            <div>
              <div className="text-slate-900 font-semibold">{user.name}</div>
              <div className="text-slate-500 text-xs">{user.email}</div>
            </div>
          )}
        </div>
        
        {/* Menu */}
        <nav className="flex-1">
          <ul className="space-y-1">
            {MENU.map((item) => {
              const showLabels = (isDesktop && !desktopSidebarCollapsed) || (!isDesktop && sidebarOpen);
              const select = (key: string) => {
                onMenuSelect(key);
                if (onClose) onClose();
                else setInternalSidebarOpen(false);
              };
              const parentActive = selectedMenu.split(':')[0] === item.key;
              return (
                <li key={item.key}>
                  <button
                    className={`w-full flex items-center ${showLabels ? 'px-4' : 'justify-center'} py-2 rounded-lg text-left transition font-semibold
                      ${selectedMenu === item.key || (parentActive && !item.children)
                        ? 'bg-slate-100 text-slate-900'
                        : parentActive
                        ? 'text-slate-900'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                    `}
                    onClick={() => select(item.key)}
                    title={item.label}
                  >
                    {item.icon}
                    {showLabels && <span className="ml-2">{item.label}</span>}
                  </button>

                  {/* Sub-items (only when labels are shown) */}
                  {item.children && showLabels && (
                    <ul className="mt-1 ml-4 space-y-0.5 border-l border-slate-200 pl-2">
                      {item.children.map((child) => (
                        <li key={child.key}>
                          <button
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition text-sm
                              ${selectedMenu === child.key
                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}
                            `}
                            onClick={() => select(child.key)}
                            title={child.label}
                          >
                            {child.icon}
                            <span>{child.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
        
        {/* Go to Home Button */}
        <div className="p-1 border-t border-slate-200">
          <Link
            href="/"
            className={`w-full flex items-center ${((isDesktop && !desktopSidebarCollapsed) || (!isDesktop && sidebarOpen)) ? 'px-3' : 'justify-center'} py-2 rounded-lg text-left transition font-semibold bg-blue-600 text-white hover:bg-blue-700`}
            title="Go to Home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            {((isDesktop && !desktopSidebarCollapsed) || (!isDesktop && sidebarOpen)) && <span className="ml-2">Go to Home</span>}
          </Link>
        </div>

        {/* Logout Button — hidden in logged-out preview */}
        {!preview && (
          <div className="p-1">
            <button
              className={`w-full flex items-center ${((isDesktop && !desktopSidebarCollapsed) || (!isDesktop && sidebarOpen)) ? 'px-3' : 'justify-center'} py-2 rounded-lg text-left transition font-semibold bg-red-600 text-white hover:bg-red-700`}
              onClick={onLogout}
              title="Logout"
            >
              <FiLogOut size={22} />
              {((isDesktop && !desktopSidebarCollapsed) || (!isDesktop && sidebarOpen)) && <span className="ml-2">Logout</span>}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
